import React from "react";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type PercentCrop,
} from "react-image-crop";
import type { Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Button } from "@components/ui/button";

interface PluginLogoCropDialogProps {
  open: boolean;
  imageSrc: string;
  mimeType?: string;
  onClose: () => void;
  onApply: (croppedImageDataUrl: string) => void;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.crossOrigin = "anonymous";
    image.src = url;
  });

const getCenteredSquareCrop = (mediaWidth: number, mediaHeight: number) => {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 80,
      },
      1,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
};

const getCroppedImageDataUrl = async (
  imageSrc: string,
  percentCrop: PercentCrop,
): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Failed to create canvas context.");
  }

  const sx = Math.max(
    0,
    Math.floor((percentCrop.x / 100) * image.naturalWidth),
  );
  const sy = Math.max(
    0,
    Math.floor((percentCrop.y / 100) * image.naturalHeight),
  );
  const width = Math.max(
    1,
    Math.floor((percentCrop.width / 100) * image.naturalWidth),
  );
  const height = Math.max(
    1,
    Math.floor((percentCrop.height / 100) * image.naturalHeight),
  );

  canvas.width = width;
  canvas.height = height;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(image, sx, sy, width, height, 0, 0, width, height);

  // Always use PNG output to keep full visual clarity.
  return canvas.toDataURL("image/png");
};

const PluginLogoCropDialog: React.FC<PluginLogoCropDialogProps> = ({
  open,
  imageSrc,
  onClose,
  onApply,
}) => {
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const [crop, setCrop] = React.useState<Crop>();
  const [completedPercentCrop, setCompletedPercentCrop] =
    React.useState<PercentCrop | null>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [zoom, setZoom] = React.useState(1);
  const [baseSize, setBaseSize] = React.useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });

  React.useEffect(() => {
    if (!open) return;
    setCrop(undefined);
    setCompletedPercentCrop(null);
    setZoom(1);
  }, [open, imageSrc]);

  const displaySize = React.useMemo(() => {
    return {
      width: Math.max(1, Math.floor(baseSize.width * zoom)),
      height: Math.max(1, Math.floor(baseSize.height * zoom)),
    };
  }, [baseSize, zoom]);

  const handleWheelZoom = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!imageRef.current) return;

    e.preventDefault();
    const direction = e.deltaY > 0 ? -1 : 1;
    setZoom((prev) => {
      const next = prev + direction * 0.08;
      return Math.max(1, Math.min(4, Number(next.toFixed(2))));
    });
  };

  const handleApply = async () => {
    if (!completedPercentCrop) return;

    setIsProcessing(true);
    try {
      const dataUrl = await getCroppedImageDataUrl(
        imageSrc,
        completedPercentCrop,
      );
      onApply(dataUrl);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-[700px]"
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Crop Logo</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="relative max-h-[460px] w-full rounded-lg bg-black/80 overflow-auto p-2"
            onWheel={handleWheelZoom}
          >
            <ReactCrop
              crop={crop}
              onChange={(nextCrop) => setCrop(nextCrop)}
              onComplete={(_, percentCrop) =>
                setCompletedPercentCrop(percentCrop)
              }
              aspect={1}
              keepSelection={true}
              ruleOfThirds={true}
              className="max-h-[440px]"
            >
              <img
                ref={imageRef}
                src={imageSrc}
                alt="Crop source"
                onLoad={(e) => {
                  const element = e.currentTarget;
                  const width = element.clientWidth;
                  const height = element.clientHeight;
                  setBaseSize({ width, height });
                  setCrop(getCenteredSquareCrop(width, height));
                }}
                width={displaySize.width}
                height={displaySize.height}
                className="block max-h-none w-auto h-auto mx-auto select-none"
              />
            </ReactCrop>
          </div>

          <p className="text-xs text-muted-foreground">
            Drag the crop handles from corners or edges to resize. Drag inside
            the selection to move it.
          </p>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleApply}
              disabled={!completedPercentCrop || isProcessing}
            >
              {isProcessing ? "Processing..." : "Apply"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PluginLogoCropDialog;
